'use client';

import { Logo } from '@eidas-pro/assets';
import { Mail, Linkedin, Github } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Link } from '@/lib/navigation';
import { ObfuscatedEmail } from '@/components/ObfuscatedEmail';
import { useAffiliate } from '@/components/affiliate/AffiliateProvider';

export function Footer() {
    const t = useTranslations('common.footer');
    const currentYear = new Date().getFullYear();
    const { enabled: affiliateEnabled } = useAffiliate();

    return (
        <footer className="bg-trust-navy text-white py-16">
            <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-12 mb-12">
                        {/* Brand */}
                        <div className="md:col-span-1">
                            <Link href="/" className="flex items-center gap-2 mb-4">
                                <Logo width={36} height={36} className="text-white" aria-label="eIDAS Pro" />
                                <span className="font-serif text-2xl">eIDAS Pro</span>
                            </Link>
                            <p className="text-white/60 text-sm leading-relaxed">{t('tagline')}</p>
                        </div>

                        {/* Product */}
                        <div>
                            <h4 className="font-semibold mb-4">{t('product.title')}</h4>
                            <ul className="space-y-2 text-sm">
                                <li>
                                    <Link
                                        href="#demo"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('product.demo')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/#pricing"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('product.pricing')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/blog"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('product.blog')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/age-verification-woocommerce"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('product.woocommerce')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/age-verification-shopify"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('product.shopify')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/age-verification-dating-apps"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('product.dating')}
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        {/* Resources */}
                        <div>
                            <h4 className="font-semibold mb-4">{t('resources.title')}</h4>
                            <ul className="space-y-2 text-sm">
                                <li>
                                    <Link
                                        href="/docs"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        Documentation
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/blog"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('resources.blog')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/support"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('resources.support')}
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        {/* Company */}
                        <div>
                            <h4 className="font-semibold mb-4">{t('company.title')}</h4>
                            <ul className="space-y-2 text-sm">
                                <li>
                                    <a
                                        href="https://linkedin.com/company/eidas-pro"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('company.about')}
                                    </a>
                                </li>
                                {affiliateEnabled && (
                                    <li>
                                        <Link
                                            href="/partners"
                                            className="text-white/70 hover:text-accent-violet transition-colors">
                                            {t('company.partners')}
                                        </Link>
                                    </li>
                                )}
                                <li>
                                    <Link
                                        href="/privacy"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('company.privacy')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/terms"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('company.terms')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/data-retention"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('company.dataRetention')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/deletion-procedures"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('company.deletionProcedures')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/impressum"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('company.impressum')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/sub-processors"
                                        className="text-white/70 hover:text-accent-violet transition-colors">
                                        {t('company.subProcessors')}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Legal / company */}
                    <div className="mt-8 space-y-1">
                        <p className="text-white/40 text-xs max-w-3xl">{t('legal.companyLine')}</p>
                        <p className="text-white/40 text-xs">
                            <Link href="/impressum" className="hover:text-white/70 transition-colors">
                                {t('legal.detailsOnRequest')}
                            </Link>
                        </p>
                    </div>

                    {/* Bottom Bar */}
                    <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-white/50 text-sm">{t('copyright', { year: currentYear })}</p>

                        {/* Social Links */}
                        <div className="flex items-center gap-4">
                            <ObfuscatedEmail
                                className="text-white/50 hover:text-accent-violet transition-colors"
                                aria-label="Email">
                                <Mail className="w-5 h-5" />
                            </ObfuscatedEmail>
                            <a
                                href="https://linkedin.com/company/eidas-pro"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white/50 hover:text-accent-violet transition-colors"
                                aria-label="LinkedIn">
                                <Linkedin className="w-5 h-5" />
                            </a>
                            <a
                                href="https://github.com/eidaspro"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white/50 hover:text-accent-violet transition-colors"
                                aria-label="GitHub">
                                <Github className="w-5 h-5" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
